// Data-driven automatic identities for non-Core buildings.

import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';

const dt = TUNING.dt;

function trigger(sim: Sim, id: number, abilityId: string, label: string): void {
  const b = sim.state.buildings.get(id);
  if (!b) return;
  sim.pushEvent({ type: 'AbilityTriggered', id, abilityId, pos: { ...b.pos }, label });
}

export function stepBuildingTraits(sim: Sim): void {
  const st = sim.state;
  for (const b of st.buildings.values()) {
    if (b.state !== 'active' || b.isCore) continue;
    const bd = buildingDef(b.defId);
    for (const trait of bd.traits ?? []) {
      const key = trait.kind;
      const ready = (b.traitCooldowns[key] ?? 0) <= 0;

      if (trait.kind === 'emergencyShoring' && ready) {
        const target = [...st.buildings.values()]
          .filter((x) => sim.areAllies(x.owner, b.owner) && x.state !== 'destroyed' && x.health / x.maxHealth <= trait.threshold && dist(x.pos, b.pos) <= trait.radius)
          .sort((a, c) => a.health / a.maxHealth - c.health / c.maxHealth || a.id - c.id)[0];
        if (target) {
          target.tempHealth = Math.max(target.tempHealth, trait.shield);
          target.tempHealthRemaining = Math.max(target.tempHealthRemaining, trait.duration);
          b.traitCooldowns[key] = trait.cooldown;
          trigger(sim, b.id, key, 'Emergency Shoring');
        }
      }

      if (trait.kind === 'periodicControl' && ready) {
        const enemies = [...st.squads.values()]
          .filter((s) => sim.areHostile(s.owner, b.owner) && dist(s.pos, b.pos) <= trait.radius)
          .sort((a, c) => dist(a.pos, b.pos) - dist(c.pos, b.pos) || a.id - c.id);
        const target = enemies[0];
        if (target) {
          if (trait.effect === 'root') target.rootedRemaining = Math.max(target.rootedRemaining, trait.duration);
          else {
            target.buffs.push({ stat: 'moveSpeed', mult: 0.65, remaining: trait.duration, source: `fear-${b.id}` });
            target.buffs.push({ stat: 'damage', mult: 0.8, remaining: trait.duration, source: `fear-${b.id}` });
            if (b.upgradeDone) {
              target.healingTakenMult = Math.min(target.healingTakenMult, 0.65);
              target.healingTakenRemaining = Math.max(target.healingTakenRemaining, 8);
            }
          }
          b.traitCooldowns[key] = trait.cooldown;
          trigger(sim, b.id, key, trait.effect === 'root' ? 'Guardian Vines' : 'Lament Pulse');
        }
      }

      if (trait.kind === 'periodicMark' && ready) {
        const target = [...st.squads.values()]
          .filter((s) => sim.areHostile(s.owner, b.owner) && dist(s.pos, b.pos) <= trait.radius)
          .sort((a, c) => unitDef(c.defId).command - unitDef(a.defId).command || a.id - c.id)[0];
        if (target) {
          target.marks.push({ markId: `${trait.markId}-${b.owner}`, remaining: trait.duration });
          b.traitCooldowns[key] = trait.cooldown;
          trigger(sim, b.id, key, 'Targeting Solution');
        }
      }

      if (trait.kind === 'intrusionBloom' && ready) {
        const enemies = [...st.squads.values()].filter((s) => sim.areHostile(s.owner, b.owner) && dist(s.pos, b.pos) <= trait.radius);
        if (enemies.length > 0) {
          for (const s of enemies) s.buffs.push({ stat: 'moveSpeed', mult: trait.slowMult, remaining: trait.duration, source: `bloom-${b.id}` });
          for (const w of st.workers.values()) {
            if (sim.areAllies(w.owner, b.owner) && dist(w.pos, b.pos) <= trait.radius) w.health = Math.min(w.maxHealth, w.health + trait.workerHeal);
          }
          b.traitCooldowns[key] = trait.cooldown;
          trigger(sim, b.id, key, 'Protective Bloom');
        }
      }

      if (trait.kind === 'travelAura') {
        for (const s of st.squads.values()) {
          if (!sim.areAllies(s.owner, b.owner) || dist(s.pos, b.pos) > trait.radius) continue;
          // Canopy Riders and flying beasts are represented by ranged/raider roles in current content.
          const tags = unitDef(s.defId).roleTags;
          if (!tags.includes('ranged') && !tags.includes('raider')) continue;
          s.buffs.push({ stat: 'moveSpeed', mult: trait.moveMult, remaining: dt * 2, source: `travel-${b.id}` });
        }
      }

      if (trait.kind === 'defendHaste' && b.siteId) {
        for (const s of st.squads.values()) {
          if (s.owner !== b.owner || s.command.kind !== 'defend' || s.command.siteId !== b.siteId) continue;
          if (dist(s.pos, b.pos) <= trait.siteRadius) continue;
          s.buffs.push({ stat: 'moveSpeed', mult: trait.moveMult, remaining: dt * 2, source: `alarm-${b.id}` });
        }
      }

      if (trait.kind === 'periodicSquadBuff' && ready) {
        const target = [...st.squads.values()]
          .filter((s) => sim.areAllies(s.owner, b.owner) && unitDef(s.defId).roleTags.includes(trait.role) && dist(s.pos, b.pos) <= trait.radius)
          .sort((a, c) => dist(a.pos, b.pos) - dist(c.pos, b.pos) || a.id - c.id)[0];
        if (target) {
          target.buffs.push({ stat: 'damage', mult: trait.damageMult, remaining: trait.duration, source: `ammo-${b.id}` });
          target.buffs.push({ stat: 'structureDamage', mult: b.upgradeDone ? 1.35 : 1.2, remaining: trait.duration, source: `ammo-${b.id}` });
          b.traitCooldowns[key] = trait.cooldown;
          trigger(sim, b.id, key, 'Ammunition Crane');
        }
      }

      if ((trait.kind === 'deathSustain' || trait.kind === 'deathShield') && ready) {
        let newest = b.lastTraitDeathAt;
        let fired = false;
        for (const death of st.recentDeaths) {
          if (death.at <= b.lastTraitDeathAt || dist(death.pos, b.pos) > trait.radius) continue;
          newest = Math.max(newest, death.at);
          fired = true;
          if (trait.kind === 'deathSustain') {
            const before = b.health;
            b.health = Math.min(b.maxHealth, b.health + trait.heal);
            const excess = Math.max(0, before + trait.heal - b.maxHealth);
            if (trait.shieldCap) b.tempHealth = Math.min(trait.shieldCap, b.tempHealth + excess);
            if (trait.cooldownReduction) {
              for (const k of Object.keys(b.traitCooldowns)) b.traitCooldowns[k] = Math.max(0, b.traitCooldowns[k] - trait.cooldownReduction);
            }
          } else {
            b.tempHealth = Math.min(trait.maxShield, b.tempHealth + trait.shield);
            b.tempHealthRemaining = Math.max(b.tempHealthRemaining, 12);
          }
        }
        b.lastTraitDeathAt = newest;
        if (fired) trigger(sim, b.id, key, trait.kind === 'deathSustain' ? 'Death Harvest' : 'Funeral Fuel');
      }
    }
  }
}
